import { useState, useEffect } from 'react';
import { getDashboardStats, getTopClients, getRevenueByService } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { DollarSign, Users, Calendar, TrendingUp, PieChart, BarChart3 } from 'lucide-react';

const formatMoney = (cents) => {
  const amount = Math.abs(cents || 0) / 100;
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(amount);
};

export default function DashboardPage() {
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState(null);
  const [topClientsByRevenue, setTopClientsByRevenue] = useState([]);
  const [topClientsByBookings, setTopClientsByBookings] = useState([]);
  const [revenueByService, setRevenueByService] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsData, topRevenue, topBookings, serviceRevenue] = await Promise.all([
          getDashboardStats(period),
          getTopClients('revenue', 10),
          getTopClients('bookings', 10),
          getRevenueByService(),
        ]);
        setStats(statsData);
        setTopClientsByRevenue(topRevenue.data || []);
        setTopClientsByBookings(topBookings.data || []);
        setRevenueByService(serviceRevenue.data || []);
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);

  const maxRevenue = Math.max(...topClientsByRevenue.map(c => c.value), 1);
  const maxBookings = Math.max(...topClientsByBookings.map(c => c.value), 1);
  const totalServiceRevenue = revenueByService.reduce((sum, s) => sum + s.revenue_cents, 0) || 1;

  return (
    <div className="flex flex-col h-full" data-testid="dashboard-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">Dashboard</h1>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="text-center text-muted-foreground py-12">Loading...</div>
        ) : (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${(stats?.total_revenue_cents || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {(stats?.total_revenue_cents || 0) < 0 && '-'}{formatMoney(stats?.total_revenue_cents)}
                  </div>
                  <p className="text-xs text-muted-foreground">All time</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Period Revenue</CardTitle>
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${(stats?.period_revenue_cents || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {(stats?.period_revenue_cents || 0) < 0 && '-'}{formatMoney(stats?.period_revenue_cents)}
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">{period}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Outstanding</CardTitle>
                  <DollarSign className="w-4 h-4 text-rose-500" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${(stats?.outstanding_balance_cents || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {(stats?.outstanding_balance_cents || 0) > 0 && '-'}{formatMoney(stats?.outstanding_balance_cents)}
                  </div>
                  <p className="text-xs text-muted-foreground">Owed by clients</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Bookings</CardTitle>
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.bookings_this_month || 0}</div>
                  <p className="text-xs text-muted-foreground">This month</p>
                </CardContent>
              </Card>
            </div>

            {/* Client Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active Clients</CardTitle>
                  <Users className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.active_clients || 0}</div>
                  <p className="text-xs text-muted-foreground">of {stats?.total_clients || 0} total</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Revenue by Service</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueByService.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No data</p>
                  ) : (
                    <div className="space-y-3">
                      {revenueByService.slice(0, 5).map((service, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: service.color }} />
                              <span className="truncate max-w-[150px]">{service.service_name}</span>
                            </div>
                            <span className="font-mono text-muted-foreground">{formatMoney(service.revenue_cents)}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${(service.revenue_cents / totalServiceRevenue) * 100}%`,
                                backgroundColor: service.color
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Clients Charts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Top Clients
                </CardTitle>
                <CardDescription>Your highest value clients</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="revenue">
                  <TabsList>
                    <TabsTrigger value="revenue">By Revenue</TabsTrigger>
                    <TabsTrigger value="bookings">By Bookings</TabsTrigger>
                  </TabsList>

                  <TabsContent value="revenue" className="mt-4">
                    {topClientsByRevenue.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No data</p>
                    ) : (
                      <div className="space-y-3">
                        {topClientsByRevenue.map((client, idx) => (
                          <div key={idx} className="flex items-center gap-4">
                            <span className="w-6 text-sm text-muted-foreground text-right">{idx + 1}</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate max-w-[200px]">{client.client_name}</span>
                                <span className="font-mono text-sm">{formatMoney(client.value)}</span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary rounded-full transition-all"
                                  style={{ width: `${(client.value / maxRevenue) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="bookings" className="mt-4">
                    {topClientsByBookings.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No data</p>
                    ) : (
                      <div className="space-y-3">
                        {topClientsByBookings.map((client, idx) => (
                          <div key={idx} className="flex items-center gap-4">
                            <span className="w-6 text-sm text-muted-foreground text-right">{idx + 1}</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate max-w-[200px]">{client.client_name}</span>
                                <span className="font-mono text-sm">{client.value} bookings</span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500 rounded-full transition-all"
                                  style={{ width: `${(client.value / maxBookings) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
